import {
  KeyboardConfig,
  Action,
  HoldTapConfig,
  isHoldTap,
  ActHoldTap,
  isMultipleActions,
} from "@/keyboard";

// Version of Omit that won't fail due to `[k: string]: unknown` in KeyboardConfig.
type OmitRobust<T, K extends PropertyKey> = {
  [P in keyof T as Exclude<P, K>]: T[P];
};

// Config representation stored internally uses different representation of HoldTap action.
export type KeyboardConfigInternal = OmitRobust<KeyboardConfig, "layers"> & {
  layers: ActionInternal[][][];
};

export type ActionInternal =
  | Exclude<Action, { HoldTap: unknown } | { MultipleActions: unknown }>
  | MultipleActionsInternal
  | HoldTapInternal;

// Nesting of HoldTap not supported
// TODO: add type-level restriction for no nesting of  MultipleActions
export type ActionInternalNested = Exclude<ActionInternal, HoldTapInternal>;

export type MultipleActionsInternal = {
  MultipleActions: ActionInternalNested[];
};
export type HoldTapInternal = {
  HoldTap: {
    hold: ActionInternalNested;
    tap: ActionInternalNested;
    // If not specified than the default options are used.
    settings?: HoldTapSettings;
  };
};
export type HoldTapSettings = {
  config: HoldTapConfig;
  tap_hold_interval: number;
  timeout: number;
};

const nonHoldTapToInternal = (
  act: Exclude<Action, { HoldTap: unknown }>
): ActionInternalNested => {
  if (!isMultipleActions(act)) return act;
  return {
    MultipleActions: act.MultipleActions.map((a) => {
      if (isHoldTap(a))
        throw Error(
          "HoldTap action only supported as the root, no nesting is allowed."
        );
      return nonHoldTapToInternal(a);
    }),
  };
};

// Type guards for the internally different Action variants must be rewritten
const has = (a: unknown, prop: string): boolean =>
  typeof a == "object" && a != null && prop in a;
export const isHoldTapInternal = (a: ActionInternal): a is HoldTapInternal =>
  has(a, "HoldTap");
export const isMultipleActionsInternal = (
  a: ActionInternal
): a is MultipleActionsInternal => has(a, "MultipleActions");

// Convert original layers configuration to the internal representation
export const layersToInternal = (
  layers: Action[][][],
  defaults: HoldTapSettings
): { layers: ActionInternal[][][]; defaults: HoldTapSettings } => {
  // Determine defaults as the most frequent ones, use stringified settings as hashmap keys.
  const toSettingsStr = (act: ActHoldTap) =>
    `${act.HoldTap.config}|${act.HoldTap.tap_hold_interval}|${act.HoldTap.timeout}`;
  const fromSettingsStr = (s: string): HoldTapSettings => {
    const [config, tap_hold_interval, timeout] = s.split("|");
    // Assume that s was the result of toStr
    return {
      config: config as HoldTapConfig,
      tap_hold_interval: Number(tap_hold_interval),
      timeout: Number(timeout),
    };
  };
  const freq = layers
    .flat(2)
    .filter(isHoldTap)
    .reduce((freq_map: { [key: string]: number }, act) => {
      const key = toSettingsStr(act);
      freq_map[key] = (freq_map[key] ?? 0) + 1;
      return freq_map;
    }, {});
  const new_defaults =
    Object.keys(freq).length == 0
      ? defaults
      : // Get the most frequent ones
        fromSettingsStr(
          Object.keys(freq).reduce((a, b) => (freq[a] > freq[b] ? a : b))
        );

  return {
    defaults: new_defaults,
    // Replace HoldTap actions such that settings=undefined when it setttings==new_defaults
    layers: layers.map((l) =>
      l.map((r) =>
        r.map((act) => {
          if (!isHoldTap(act)) return nonHoldTapToInternal(act);

          if (isHoldTap(act.HoldTap.hold) || isHoldTap(act.HoldTap.tap))
            throw Error("Nesting HoldTap actions is not supported.");

          let settings: HoldTapSettings | undefined = {
            config: act.HoldTap.config,
            tap_hold_interval: act.HoldTap.tap_hold_interval,
            timeout: act.HoldTap.timeout,
          };
          if (
            act.HoldTap.config == new_defaults.config &&
            act.HoldTap.tap_hold_interval == new_defaults.tap_hold_interval &&
            act.HoldTap.timeout == new_defaults.timeout
          ) {
            settings = undefined;
          }
          return {
            HoldTap: {
              hold: nonHoldTapToInternal(act.HoldTap.hold),
              tap: nonHoldTapToInternal(act.HoldTap.tap),
              settings,
            },
          };
        })
      )
    ),
  };
};

export const layersFromInternal = (
  layers: ActionInternal[][][],
  defaults: HoldTapSettings
): Action[][][] => {
  return layers.map((l) =>
    l.map((r) =>
      r.map((act) => {
        if (!isHoldTapInternal(act)) return act;
        const a = act as HoldTapInternal;
        const settings = a.HoldTap.settings ?? defaults;
        return {
          HoldTap: {
            hold: a.HoldTap.hold,
            tap: a.HoldTap.tap,
            ...settings,
          },
        };
      })
    )
  );
};
